// Validation utilities for the signup form

export interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  classGrade?: string;
  general?: string;
}

/**
 * Validate first name
 */
export const validateFirstName = (firstName: string): string | undefined => {
  if (!firstName.trim()) {
    return 'First name is required';
  }
  
  if (firstName.trim().length < 2) {
    return 'First name must be at least 2 characters long';
  }
  
  if (firstName.trim().length > 50) {
    return 'First name must be less than 50 characters';
  }
  
  // Check for numbers in name
  if (/\d/.test(firstName)) {
    return 'First name cannot contain numbers';
  }
  
  // Check for special characters (allow spaces, hyphens, apostrophes)
  if (!/^[a-zA-Z\s'-]+$/.test(firstName)) {
    return 'First name can only contain letters, spaces, hyphens, and apostrophes';
  }
  
  return undefined;
};

/**
 * Validate last name
 */
export const validateLastName = (lastName: string): string | undefined => {
  if (!lastName.trim()) {
    return 'Last name is required';
  }
  
  if (lastName.trim().length < 2) {
    return 'Last name must be at least 2 characters long';
  }
  
  if (lastName.trim().length > 50) {
    return 'Last name must be less than 50 characters';
  }
  
  // Check for numbers in name
  if (/\d/.test(lastName)) {
    return 'Last name cannot contain numbers';
  }
  
  // Check for special characters (allow spaces, hyphens, apostrophes)
  if (!/^[a-zA-Z\s'-]+$/.test(lastName)) {
    return 'Last name can only contain letters, spaces, hyphens, and apostrophes';
  }
  
  return undefined;
};

/**
 * Validate email address
 */
export const validateEmail = (email: string): string | undefined => {
  if (!email.trim()) {
    return 'Email is required';
  }
  
  // RFC 5322 compliant email regex (simplified)
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address';
  }
  
  if (email.length > 254) {
    return 'Email address is too long';
  }
  
  return undefined;
};

/**
 * Validate password
 */
export const validatePassword = (password: string): string | undefined => {
  if (!password) {
    return 'Password is required';
  }
  
  if (password.length < 8) {
    return 'Password must be at least 8 characters long';
  }
  
  if (password.length > 128) {
    return 'Password must be less than 128 characters';
  }
  
  // Check for at least one uppercase letter
  if (!/[A-Z]/.test(password)) {
    return 'Password must contain at least one uppercase letter';
  }
  
  // Check for at least one lowercase letter
  if (!/[a-z]/.test(password)) {
    return 'Password must contain at least one lowercase letter';
  }
  
  // Check for at least one number
  if (!/\d/.test(password)) {
    return 'Password must contain at least one number';
  }
  
  // Check for at least one special character
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    return 'Password must contain at least one special character';
  }
  
  return undefined;
};

/**
 * Validate confirm password
 */
export const validateConfirmPassword = (password: string, confirmPassword: string): string | undefined => {
  if (!confirmPassword) {
    return 'Please confirm your password';
  }
  
  if (password !== confirmPassword) {
    return 'Passwords do not match';
  }
  
  return undefined;
};

/**
 * Validate all form fields
 */
export const validateSignupForm = (
  firstName: string,
  lastName: string,
  email: string,
  password: string,
  confirmPassword: string,
  userType?: 'student' | 'teacher',
  classGrade?: string
): FormErrors => {
  const errors: FormErrors = {};
  
  const firstNameError = validateFirstName(firstName);
  if (firstNameError) errors.firstName = firstNameError;
  
  const lastNameError = validateLastName(lastName);
  if (lastNameError) errors.lastName = lastNameError;
  
  const emailError = validateEmail(email);
  if (emailError) errors.email = emailError;
  
  const passwordError = validatePassword(password);
  if (passwordError) errors.password = passwordError;
  
  const confirmPasswordError = validateConfirmPassword(password, confirmPassword);
  if (confirmPasswordError) errors.confirmPassword = confirmPasswordError;
  
  if (!userType) {
    errors.general = 'Please select whether you are a student or teacher';
  }
  
  // Validate class/grade for students
  if (userType === 'student' && (!classGrade || !classGrade.trim())) {
    errors.classGrade = 'Please select your current class/grade';
  }
  
  return errors;
};

/**
 * Check if the form has any validation errors
 */
export const hasValidationErrors = (errors: FormErrors): boolean => {
  return Object.keys(errors).length > 0;
};
